/**
 * Settings page — single surface for the three permission asks we ship in
 * v1: Notifications, Location, Contacts. Each toggle is owned by its
 * respective module (PushToggle from @ah/notifications/mobile, Location +
 * Contacts from @ah/people/mobile) so this file just composes them.
 *
 * Apple cares that each permission has a clear, user-initiated entry point
 * and that the granted data is actually used by the app. Every toggle on
 * this page satisfies both: tapping is explicit consent, and each one
 * writes the data to the server where it can feed into ranking later.
 */

import { useState } from 'react';
import { Bell, Trash2 } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { Header } from '@ah/design/mobile';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  useGql,
} from '@ah/design';
import { PushToggle } from '@ah/notifications/mobile';
import { SEND_TEST_NOTIFICATION } from '@ah/notifications/shared';
import { LocationToggle, ContactsToggle } from '@ah/people/mobile';
import { DELETE_MY_ACCOUNT_M } from '@ah/people/shared';
import { BlockedUsersList } from '@ah/moderation/mobile';
import { useMe } from '../useAuth';
import { clearSession } from '../accounts';
import { env } from '../env';

export function Settings() {
  const { data: me } = useMe();
  return (
    <div className="min-h-dvh bg-background">
      <Header title="Settings" />
      <main className="pb-12">
        <section className="mt-2">
          <h2 className="px-4 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Permissions
          </h2>
          <div className="border-y border-border bg-card">
            <PushToggle />
            <LocationToggle />
            <ContactsToggle />
          </div>
          <p className="px-4 mt-3 text-xs text-muted-foreground">
            We only use these on your device until you turn them on. Each
            switch can be turned off any time — and revoked entirely in your
            phone&apos;s Settings app.
          </p>
        </section>

        {/* Admin-only diagnostics. Lets us verify push delivery without
            needing access to the server console or a second logged-in
            account. Hidden from regular users to keep Settings clean. */}
        {me?.isAdmin && <AdminDiagnostics />}

        {/* List of users this user has blocked, with unblock action.
            Renders nothing when empty so the page stays clean for the
            common case. Required by Apple App Review (Guideline 1.2). */}
        <BlockedUsersList />

        {/* Account deletion — required by Apple App Review (Guideline
            5.1.1(v)). Must be reachable in-app, not via a website. */}
        <DeleteAccountSection />

        {/* Build identity. Visible to everyone so support / reviewers
            can quote a stable handle when something looks wrong ("on
            build f76d1d6, built 3 hours ago"). The relative time
            updates implicitly on each render — no live ticker needed
            since this page doesn't change often. Admins get the
            absolute UTC timestamp too. */}
        <BuildLine isAdmin={me?.isAdmin ?? false} />
      </main>
    </div>
  );
}

function DeleteAccountSection() {
  const gql = useGql();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: () => gql.request<{ deleteMyAccount: boolean }>(DELETE_MY_ACCOUNT_M),
    onSuccess: () => {
      clearSession();
      window.location.href = '/';
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : String(err));
    },
  });

  return (
    <section className="mt-8">
      <h2 className="px-4 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Account
      </h2>
      <div className="border-y border-border bg-card">
        <button
          type="button"
          onClick={() => { setError(null); setOpen(true); }}
          className="w-full flex items-center gap-3 px-4 py-3 text-sm text-left hover:bg-accent"
        >
          <div className="size-9 rounded-full bg-destructive/10 flex items-center justify-center shrink-0">
            <Trash2 size={18} className="text-destructive" />
          </div>
          <div className="flex-1">
            <p className="font-semibold text-destructive">Delete account</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Permanently delete your Underlings account and all your data.
            </p>
          </div>
        </button>
      </div>

      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete your Underlings account?</AlertDialogTitle>
            <AlertDialogDescription>
              This will sign you out on every device and schedule your profile,
              messages, circle memberships, and contacts for permanent deletion
              after 30 days. Sign in again within 30 days to cancel — after that
              the data cannot be recovered.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={mutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={mutation.isPending}
              onClick={(e) => { e.preventDefault(); mutation.mutate(); }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {mutation.isPending ? 'Deleting…' : 'Delete account'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

function BuildLine({ isAdmin }: { isAdmin: boolean }) {
  const version = env.VITE_BUILD_VERSION;
  const timestamp = env.VITE_BUILD_TIMESTAMP;
  const relative = formatRelativeAge(timestamp);
  return (
    <p className="px-4 mt-6 text-xs text-muted-foreground">
      Build <code className="font-mono">{version}</code>
      {relative && <> · built {relative}</>}
      {isAdmin && timestamp && (
        <span className="block mt-0.5 font-mono opacity-70">{timestamp}</span>
      )}
    </p>
  );
}

/**
 * "7 minutes ago", "3 hours ago", "2 days ago". Returns null if the
 * timestamp is missing or unparseable so the caller can render nothing.
 * Capped at days because the build is essentially never older than a
 * week in practice — OTAs ship every few hours, and a stale shell with
 * its built-in stub will pull a fresh bundle on first launch.
 */
function formatRelativeAge(iso: string): string | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  const diffSec = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (diffSec < 60) return 'just now';
  const min = Math.round(diffSec / 60);
  if (min < 60) return `${min} minute${min === 1 ? '' : 's'} ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} hour${hr === 1 ? '' : 's'} ago`;
  const day = Math.round(hr / 24);
  return `${day} day${day === 1 ? '' : 's'} ago`;
}

function AdminDiagnostics() {
  const gql = useGql();
  const [toast, setToast] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: () => gql.request<{ sendTestNotification: boolean }>(SEND_TEST_NOTIFICATION),
    onSuccess: () => {
      setToast('Test push sent — should arrive in a moment.');
      setTimeout(() => setToast(null), 4000);
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : String(err);
      setToast(`Send failed: ${msg.slice(0, 120)}`);
      setTimeout(() => setToast(null), 6000);
    },
  });

  return (
    <section className="mt-6">
      <h2 className="px-4 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Admin
      </h2>
      <div className="border-y border-border bg-card">
        <button
          type="button"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
          className="w-full flex items-center gap-3 px-4 py-3 text-sm text-left disabled:opacity-60 hover:bg-accent"
        >
          <div className="size-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <Bell size={18} className="text-primary" />
          </div>
          <div className="flex-1">
            <p className="font-semibold">Send test push</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {mutation.isPending ? 'Sending…' : 'Fires a push to every device registered to your account.'}
            </p>
          </div>
        </button>
      </div>
      {toast && (
        <p className="px-4 mt-3 text-xs text-muted-foreground">{toast}</p>
      )}
    </section>
  );
}
